/**
 * UserHistoryManager - Centralized utility for managing user audit history
 * 
 * Tracks all changes to user data including:
 * - Email changes
 * - Avatar changes
 * - Display name changes
 * - Server nickname changes
 * - Role changes
 * - Server joins/leaves
 * 
 * History is stored in localStorage with key: userHistory_{discordId}
 */

// Change types enum
export const ChangeType = {
    EMAIL: 'email',
    AVATAR: 'avatar',
    DISPLAY_NAME: 'displayName',
    NICKNAME_SERVIDOR: 'nickname_servidor',
    ROLE: 'role',
    SERVER_JOIN: 'server_join',
    SERVER_LEAVE: 'server_leave',
    FROZEN_DATA_ADMIN: 'frozen_data_admin',
    ACCOUNT_CREATED: 'account_created',
    ACCOUNT_MIGRATED: 'account_migrated'
};

/**
 * Generate a simple UUID v4
 */
function generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

/**
 * Get the localStorage key for a user's history
 */
function getHistoryKey(userId) {
    return `userHistory_${userId}`;
}

/**
 * Load history from localStorage
 */
function loadHistory(userId) {
    try {
        const key = getHistoryKey(userId);
        const data = localStorage.getItem(key);
        return data ? JSON.parse(data) : [];
    } catch (error) {
        console.error('Error loading user history:', error);
        return [];
    }
}

/**
 * Save history to localStorage
 */
function saveHistory(userId, history) {
    try {
        const key = getHistoryKey(userId);
        localStorage.setItem(key, JSON.stringify(history));
        return true;
    } catch (error) {
        console.error('Error saving user history:', error);
        return false;
    }
}

/**
 * Add a new history entry
 * 
 * @param {string} userId - Discord ID of the user
 * @param {string} changeType - Type of change (use ChangeType enum)
 * @param {any} oldValue - Previous value
 * @param {any} newValue - New value
 * @param {object} metadata - Additional metadata
 * @returns {object} The created history entry
 */
export function addHistoryEntry(userId, changeType, oldValue, newValue, metadata = {}) {
    const history = loadHistory(userId);

    const entry = {
        id: generateUUID(),
        userId,
        changeType,
        oldValue,
        newValue,
        timestamp: new Date().toISOString(),
        ipAddress: null, // Placeholder for future backend implementation
        userAgent: navigator.userAgent,
        isAdminChange: metadata.isAdminChange || false,
        adminId: metadata.adminId || null,
        ...metadata // Include any additional metadata
    };

    history.push(entry);
    saveHistory(userId, history);

    console.log(`[History] Added entry for ${userId}:`, entry);

    return entry;
}

/**
 * Get history for a user with optional filtering
 * 
 * @param {string} userId - Discord ID of the user
 * @param {object} filters - Optional filters
 * @param {string} filters.changeType - Filter by change type
 * @param {Date} filters.startDate - Filter by start date
 * @param {Date} filters.endDate - Filter by end date
 * @param {boolean} filters.adminOnly - Show only admin changes
 * @param {number} filters.limit - Limit number of results
 * @returns {array} Filtered history entries
 */
export function getHistory(userId, filters = {}) {
    let history = loadHistory(userId);

    // Apply filters
    if (filters.changeType) {
        history = history.filter(entry => entry.changeType === filters.changeType);
    }

    if (filters.startDate) {
        const startTime = new Date(filters.startDate).getTime();
        history = history.filter(entry => new Date(entry.timestamp).getTime() >= startTime);
    }

    if (filters.endDate) {
        const endTime = new Date(filters.endDate).getTime();
        history = history.filter(entry => new Date(entry.timestamp).getTime() <= endTime);
    }

    if (filters.adminOnly) {
        history = history.filter(entry => entry.isAdminChange === true);
    }

    // Sort by timestamp (newest first)
    history.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    // Apply limit
    if (filters.limit && filters.limit > 0) {
        history = history.slice(0, filters.limit);
    }

    return history;
}

/**
 * Compare current data with new data and automatically track changes
 * 
 * @param {string} userId - Discord ID of the user
 * @param {object} currentData - Current user data
 * @param {object} newData - New data from Discord
 * @returns {array} Array of created history entries
 */
export function compareAndTrack(userId, currentData, newData) {
    const changes = [];

    // Track email changes
    if (currentData.email !== newData.email) {
        changes.push(addHistoryEntry(
            userId,
            ChangeType.EMAIL,
            currentData.email,
            newData.email
        ));
    }

    // Track avatar changes
    if (currentData.avatar !== newData.avatar) {
        changes.push(addHistoryEntry(
            userId,
            ChangeType.AVATAR,
            currentData.avatar,
            newData.avatar
        ));
    }

    // Track display name changes
    if (currentData.name !== newData.name) {
        changes.push(addHistoryEntry(
            userId,
            ChangeType.DISPLAY_NAME,
            currentData.name,
            newData.name
        ));
    }

    // Track server nickname changes
    if (currentData.cities && newData.cities) {
        const currentCities = currentData.cities || [];
        const newCities = newData.cities || [];

        // Create maps for easier comparison
        const currentCityMap = {};
        currentCities.forEach(city => {
            currentCityMap[city.city] = city;
        });

        const newCityMap = {};
        newCities.forEach(city => {
            newCityMap[city.city] = city;
        });

        // Check for nickname changes in existing servers
        newCities.forEach(newCity => {
            const currentCity = currentCityMap[newCity.city];

            if (currentCity) {
                // Server exists in both - check for nickname change
                if (currentCity.characterName !== newCity.characterName) {
                    changes.push(addHistoryEntry(
                        userId,
                        ChangeType.NICKNAME_SERVIDOR,
                        currentCity.characterName,
                        newCity.characterName,
                        { servidor: newCity.city }
                    ));
                }

                // Check for role changes
                const currentRoles = JSON.stringify(currentCity.roles || []);
                const newRoles = JSON.stringify(newCity.roles || []);

                if (currentRoles !== newRoles) {
                    changes.push(addHistoryEntry(
                        userId,
                        ChangeType.ROLE,
                        currentCity.roles,
                        newCity.roles,
                        { servidor: newCity.city }
                    ));
                }
            } else {
                // New server joined
                changes.push(addHistoryEntry(
                    userId,
                    ChangeType.SERVER_JOIN,
                    null,
                    newCity.city,
                    { cityData: newCity }
                ));
            }
        });

        // Check for servers left
        currentCities.forEach(currentCity => {
            if (!newCityMap[currentCity.city]) {
                changes.push(addHistoryEntry(
                    userId,
                    ChangeType.SERVER_LEAVE,
                    currentCity.city,
                    null,
                    { cityData: currentCity }
                ));
            }
        });
    }

    if (changes.length > 0) {
        console.log(`[History] Tracked ${changes.length} changes for user ${userId}`);
    }

    return changes;
}

/**
 * Get history statistics for a user
 * 
 * @param {string} userId - Discord ID of the user
 * @returns {object} Statistics object
 */
export function getHistoryStats(userId) {
    const history = loadHistory(userId);

    if (history.length === 0) {
        return {
            totalChanges: 0,
            lastChange: null,
            firstChange: null,
            changesByType: {},
            adminChanges: 0
        };
    }

    // Sort by timestamp
    const sorted = [...history].sort((a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );

    // Count changes by type
    const changesByType = {};
    let adminChanges = 0;

    history.forEach(entry => {
        changesByType[entry.changeType] = (changesByType[entry.changeType] || 0) + 1;
        if (entry.isAdminChange) {
            adminChanges++;
        }
    });

    return {
        totalChanges: history.length,
        lastChange: sorted[sorted.length - 1],
        firstChange: sorted[0],
        changesByType,
        adminChanges
    };
}

/**
 * Clear all history for a user (admin only - use with caution)
 * 
 * @param {string} userId - Discord ID of the user
 * @returns {boolean} Success status
 */
export function clearHistory(userId) {
    try {
        const key = getHistoryKey(userId);
        localStorage.removeItem(key);
        console.log(`[History] Cleared all history for user ${userId}`);
        return true;
    } catch (error) {
        console.error('Error clearing user history:', error);
        return false;
    }
}

/**
 * Export history as JSON
 * 
 * @param {string} userId - Discord ID of the user
 * @returns {string} JSON string of history
 */
export function exportHistoryJSON(userId) {
    const history = loadHistory(userId);
    return JSON.stringify(history, null, 2);
}

/**
 * Export history as CSV
 * 
 * @param {string} userId - Discord ID of the user
 * @returns {string} CSV string of history
 */
export function exportHistoryCSV(userId) {
    const history = loadHistory(userId);

    if (history.length === 0) {
        return 'No history available';
    }

    // CSV headers
    const headers = ['ID', 'Timestamp', 'Change Type', 'Old Value', 'New Value', 'Admin Change', 'Admin ID'];

    // CSV rows
    const rows = history.map(entry => [
        entry.id,
        entry.timestamp,
        entry.changeType,
        JSON.stringify(entry.oldValue),
        JSON.stringify(entry.newValue),
        entry.isAdminChange ? 'Yes' : 'No',
        entry.adminId || 'N/A'
    ]);

    // Combine headers and rows
    const csv = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    return csv;
}

/**
 * Initialize history for a new user
 * 
 * @param {string} userId - Discord ID of the user
 * @param {object} initialData - Initial user data
 * @returns {object} The created history entry
 */
export function initializeHistory(userId, initialData) {
    return addHistoryEntry(
        userId,
        ChangeType.ACCOUNT_CREATED,
        null,
        initialData,
        {
            message: 'Account created and data frozen',
            frozenAt: new Date().toISOString()
        }
    );
}

/**
 * Create migration entry for existing users
 * 
 * @param {string} userId - Discord ID of the user
 * @param {object} frozenData - Data that was frozen during migration
 * @returns {object} The created history entry
 */
export function createMigrationEntry(userId, frozenData) {
    return addHistoryEntry(
        userId,
        ChangeType.ACCOUNT_MIGRATED,
        null,
        frozenData,
        {
            message: 'Account migrated to new data freeze system',
            migratedAt: new Date().toISOString()
        }
    );
}
